import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { Btn, Card, Field, ListEditor, MediaInput, PageTitle } from "@/components/admin/AdminUI";

const ICONS = [["waves", "Agua"], ["flame", "Fuego"], ["bath", "Tinaja"], ["car", "Auto"], ["utensils", "Comida"], ["droplets", "Gotas"], ["trees", "Árboles"], ["wifi", "Wi-Fi"]];

function ObjFields({ obj, onChange, fields, prefix }) {
  return <div className="grid sm:grid-cols-2 gap-3">{fields.map((f) => <Field key={f.key} f={f} value={obj?.[f.key]} onChange={(v) => onChange({ ...obj, [f.key]: v })} testid={`${prefix}-${f.key}`} />)}</div>;
}

export default function AdminContent() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const c = s.content;
  const setC = (v) => setS({ ...s, content: v });
  const save = async () => {
    try { const r = await api.put("/admin/settings", { content: s.content, contact: s.contact, bank: s.bank, policies: s.policies }); setS(r.data); toast.success("Contenido guardado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-content-page">
      <PageTitle title="Contenido del sitio"><Btn onClick={save} data-testid="save-content-btn">Guardar cambios</Btn></PageTitle>
      <Tabs defaultValue="portada">
        <TabsList className="mb-6 flex-wrap h-auto">
          {[["portada", "Portada y textos"], ["galeria", "Galería"], ["instalaciones", "Instalaciones"], ["opiniones", "Opiniones"], ["faq", "Preguntas"], ["contacto", "Contacto y redes"], ["banco", "Datos bancarios"], ["politicas", "Políticas"]].map(([k, l]) =>
            <TabsTrigger key={k} value={k} data-testid={`content-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="portada"><Card>
          <ObjFields obj={c} onChange={setC} prefix="content" fields={[
            { key: "hero_title", label: "Nombre / título" }, { key: "hero_subtitle", label: "Frase de portada" },
            { key: "hero_image", label: "Imagen de portada", type: "media" }, { key: "hero_video", label: "Video de portada (opcional)", type: "media" },
            { key: "about_title", label: "Título instalaciones", wide: true }, { key: "about_text", label: "Texto instalaciones", type: "textarea", wide: true },
            { key: "arrival_instructions", label: "Instrucciones de llegada (correo de confirmación)", type: "textarea", wide: true },
            { key: "seo_title", label: "Título SEO", wide: true }, { key: "seo_description", label: "Descripción SEO", type: "textarea", wide: true }]} />
        </Card></TabsContent>
        <TabsContent value="galeria"><Card>
          <p className="text-sm text-muted-foreground mb-4">Sube fotos, videos (incluidos videos de dron) o pega un enlace de YouTube.</p>
          <GalleryEditor items={c.gallery} onChange={(v) => setC({ ...c, gallery: v })} />
        </Card></TabsContent>
        <TabsContent value="instalaciones"><Card>
          <ListEditor testid="amenities-editor" items={c.amenities} onChange={(v) => setC({ ...c, amenities: v })} newItem={{ title: "", description: "", icon: "trees" }}
            fields={[{ key: "title", label: "Nombre" }, { key: "icon", label: "Ícono", type: "select", options: ICONS }, { key: "description", label: "Descripción", type: "textarea", wide: true }]} />
        </Card></TabsContent>
        <TabsContent value="opiniones"><Card>
          <ListEditor testid="reviews-editor" items={c.reviews} onChange={(v) => setC({ ...c, reviews: v })} newItem={{ name: "", text: "", rating: 5 }}
            fields={[{ key: "name", label: "Nombre" }, { key: "rating", label: "Estrellas (1-5)", type: "number" }, { key: "text", label: "Opinión", type: "textarea", wide: true }]} />
        </Card></TabsContent>
        <TabsContent value="faq"><Card>
          <ListEditor testid="faqs-editor" items={c.faqs} onChange={(v) => setC({ ...c, faqs: v })} newItem={{ q: "", a: "" }}
            fields={[{ key: "q", label: "Pregunta", wide: true }, { key: "a", label: "Respuesta", type: "textarea", wide: true }]} />
        </Card></TabsContent>
        <TabsContent value="contacto"><Card>
          <ObjFields obj={s.contact} onChange={(v) => setS({ ...s, contact: v })} prefix="contact" fields={[
            { key: "whatsapp", label: "WhatsApp (con código país, ej: 56912345678)" }, { key: "email", label: "Correo de contacto" },
            { key: "instagram_url", label: "Instagram (URL)" }, { key: "maps_link", label: "Enlace Google Maps (Cómo llegar)" },
            { key: "maps_embed_url", label: "URL del mapa integrado (embed)", wide: true }, { key: "address", label: "Dirección", wide: true }]} />
        </Card></TabsContent>
        <TabsContent value="banco"><Card>
          <ObjFields obj={s.bank} onChange={(v) => setS({ ...s, bank: v })} prefix="bank" fields={[
            { key: "bank_name", label: "Banco" }, { key: "account_type", label: "Tipo de cuenta" }, { key: "account_number", label: "Número de cuenta" },
            { key: "holder", label: "Titular" }, { key: "rut", label: "RUT" }, { key: "email", label: "Correo" }]} />
        </Card></TabsContent>
        <TabsContent value="politicas"><Card>
          <ObjFields obj={s.policies} onChange={(v) => setS({ ...s, policies: v })} prefix="policies" fields={[
            { key: "text", label: "Política de reserva y cancelación", type: "textarea", wide: true },
            { key: "change_deadline_days", label: "Plazo mínimo para cambios de fecha (días)", type: "number" },
            { key: "terms_text", label: "Términos y condiciones", type: "textarea", wide: true },
            { key: "privacy_text", label: "Política de privacidad", type: "textarea", wide: true }]} />
        </Card></TabsContent>
      </Tabs>
    </div>
  );
}

function QuickUpload({ onAdd }) {
  const [v, setV] = useState("");
  return <MediaInput value={v} testid="gallery-quick-upload" onChange={setV} onType={(t, u) => { onAdd(u, t); setV(""); }} />;
}

function GalleryEditor({ items = [], onChange }) {
  return (
    <div className="space-y-3" data-testid="gallery-editor">
      <ListEditor testid="gallery-list" items={items} onChange={onChange} newItem={{ url: "", type: "image", caption: "" }}
        fields={[{ key: "url", label: "Archivo", type: "media", wide: true }, { key: "type", label: "Tipo", type: "select", options: [["image", "Foto"], ["video", "Video"]] }, { key: "caption", label: "Leyenda" }]} />
      <div className="rounded-2xl border border-dashed border-border p-4">
        <p className="text-sm font-semibold mb-2">Subida rápida</p>
        <QuickUpload onAdd={(url, type) => onChange([...items, { id: crypto.randomUUID(), url, type, caption: "" }])} />
      </div>
    </div>
  );
}
